/**
 * CreateWebhookEndpointModal - registers a new outbound webhook endpoint.
 *
 * Fields:
 *  - Name (required).
 *  - URL (required, must parse as URL).
 *  - event_types: multi-select of the SPEC §11 event names. An empty
 *    selection means "All events".
 *  - Description (optional).
 *
 * The parent runs the mutation and is responsible for showing the
 * resulting `secret` via the RevealOnceModal.
 */
import { useState, type FormEvent } from "react";

import { Alert, Button, Input, Modal, Textarea } from "@/components/ui";
import type {
  WebhookEndpointCreateRequest,
  WebhookEventType,
} from "@/types/api";

const ALL_EVENTS: WebhookEventType[] = [
  "ticket.created",
  "ticket.updated",
  "ticket.status_changed",
  "ticket.assigned",
  "ticket.spam_marked",
  "ticket.deleted",
  "comment.added",
  "attachment.added",
];

export interface CreateWebhookEndpointModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (body: WebhookEndpointCreateRequest) => Promise<void> | void;
  isSubmitting?: boolean;
  error?: string | null;
}

export function CreateWebhookEndpointModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  error,
}: CreateWebhookEndpointModalProps): JSX.Element {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<Set<WebhookEventType>>(
    new Set(),
  );
  const [description, setDescription] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const resetForm = () => {
    setName("");
    setUrl("");
    setSelectedEvents(new Set());
    setDescription("");
    setLocalError(null);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const toggleEvent = (event: WebhookEventType) => {
    setSelectedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(event)) next.delete(event);
      else next.add(event);
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!name.trim()) {
      setLocalError("Name is required.");
      return;
    }
    if (!url.trim()) {
      setLocalError("URL is required.");
      return;
    }
    try {
      // Validate URL parses. The backend re-validates.
      new URL(url.trim());
    } catch {
      setLocalError("URL is not valid.");
      return;
    }

    const body: WebhookEndpointCreateRequest = {
      name: name.trim(),
      url: url.trim(),
      event_types: Array.from(selectedEvents),
      description: description.trim() || null,
    };

    try {
      await onSubmit(body);
      resetForm();
    } catch {
      // Parent surfaces via `error` prop.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add webhook endpoint"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {(error || localError) && (
          <Alert variant="error">{error ?? localError}</Alert>
        )}

        <Input
          id="webhook-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. AI orchestrator"
          autoFocus
        />

        <Input
          id="webhook-url"
          label="URL"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/buyist-webhook"
          helperText="HTTPS endpoint that will receive HMAC-signed POSTs."
        />

        <div>
          <span className="block text-sm font-medium text-neutral-700">
            Subscribed events
          </span>
          <p className="mt-1 text-xs text-neutral-500">
            Select the events to deliver. Leave all unchecked to receive
            every event.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {ALL_EVENTS.map((evt) => (
              <label
                key={evt}
                className="inline-flex items-center gap-2 rounded-md border border-neutral-200 px-3 py-2"
              >
                <input
                  type="checkbox"
                  checked={selectedEvents.has(evt)}
                  onChange={() => toggleEvent(evt)}
                  className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="font-mono text-xs text-neutral-800">
                  {evt}
                </span>
              </label>
            ))}
          </div>
        </div>

        <Textarea
          id="webhook-description"
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Add endpoint
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default CreateWebhookEndpointModal;
